import { create } from 'zustand'
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware'
import { UserRole } from '@/constants/roles.constant'
import { PERSIST_STORE_NAME } from '@/constants/app.constant'
import { TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY } from '@/constants/api.constant'
import type { GlobalError } from '@/@types/errors'

// Re-export User type from auth slice for backward compatibility
export interface User {
  id: string
  email: string
  role: UserRole
  firstName: string
  lastName: string
  phone?: string
  schoolId: string
  photoUrl?: string
  isActive?: boolean
  createdAt?: string
  updatedAt?: string
  teacher?: any
  parent?: {
    id: string
    students: {
      id: string
      relationship: string
      student: {
        id: string
        userId: string | null
        schoolId: string
        studentId: string
        firstName: string
        lastName: string
        dob: string
        gender: string
        enrollmentDate: string
        isActive: boolean
      }
    }[]
  }
  accountant?: any
}

export interface Term {
  id: string
  name: string
  isPaid: boolean
  startDate: string
  endDate: string
}

export interface SyncItem {
  id: string
  type: 'attendance' | 'checkin' | 'marks'
  payload: any
  timestamp: string
  retries: number
}

// SMS-Specific Types
export interface Student {
  id: string
  name: string
  rollNumber: string
  classId: string
  className: string
  grade: string
  photoUrl?: string
  parentIds: string[]
  todayAttendance?: 'PRESENT' | 'ABSENT' | 'LATE' | 'PENDING'
  recentAlerts?: number
  lastSeen?: string
}

export interface Class {
  id: string
  name: string
  subject: string
  grade: string
  teacherId: string
  teacherName: string
  room: string
  schedule: {
    day: string
    startTime: string
    endTime: string
  }[]
  studentCount: number
}

export interface Subject {
  id: string
  name: string
  code: string
  grade: string
  description?: string
}

export interface AttendanceRecord {
  id: string
  studentId: string
  classId: string
  date: string
  status: 'PRESENT' | 'ABSENT' | 'LATE' | 'EXCUSED'
  remarks?: string
  recordedBy: string
  recordedAt: string
}

export interface MarkEntry {
  id: string
  studentId: string
  examId: string
  subjectId: string
  score: number
  grade: string
  remarks?: string
  submittedAt: string
}

export interface Exam {
  id: string
  name: string
  classId: string
  subjectId: string
  examType: 'quiz' | 'test' | 'midterm' | 'final' | 'assignment'
  totalMarks: number
  date: string
  status: 'draft' | 'published' | 'graded'
  createdBy: string
}

export interface Invoice {
  id: string
  studentId: string
  amount: number
  description: string
  dueDate: string
  status: 'pending' | 'paid' | 'overdue'
  createdAt: string
  paidAt?: string
}

export interface SchoolEvent {
  id: string
  title: string
  description: string
  date: string
  type: 'holiday' | 'exam' | 'meeting' | 'event'
  affectedClasses?: string[]
}

export interface AppStore {
  // Auth identity
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  
  // App-wide Context
  activeTerm: Term | null
  
  // Auth Actions
  setAuth: (user: User, accessToken: string, refreshToken: string) => void
  clearAuth: () => void
  setCredentials: (user: User, accessToken: string, refreshToken: string) => void
  signOutSuccess: () => void
  updateUser: (user: Partial<User>) => void
  syncFromSession: (session: any) => void
  
  // Term Actions
  setActiveTerm: (term: Term | null) => void
  
  // Modals & Guards
  termUnpaidModalOpen: boolean
  termUnpaidDetails: any | null
  showTermUnpaidModal: (details: any) => void
  hideTermUnpaidModal: () => void
  
  // Offline Sync Engine
  isOffline: boolean
  setOffline: (status: boolean) => void
  offlineQueue: SyncItem[]
  addToQueue: (item: SyncItem) => void
  removeFromQueue: (id: string) => void
  updateQueueItem: (id: string, updates: Partial<SyncItem>) => void
  clearQueue: () => void
  isSyncing: boolean
  setSyncing: (status: boolean) => void
  
  // UI State
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  
  // Global UI State
  globalLoading: boolean
  globalLoadingMessage: string | null
  globalError: GlobalError | null
  setGlobalLoading: (loading: boolean, message?: string) => void
  setGlobalError: (error: GlobalError | null) => void
  clearGlobalError: () => void
  
  // Notification State
  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp'>) => void
  removeNotification: (id: string) => void
  clearNotifications: () => void

  // SMS-Specific State
  // Academic Data
  classes: Class[]
  students: Student[]
  subjects: Subject[]
  setClasses: (classes: Class[]) => void
  setStudents: (students: Student[]) => void
  setSubjects: (subjects: Subject[]) => void
  
  // Attendance
  attendanceRecords: AttendanceRecord[]
  setAttendanceRecords: (records: AttendanceRecord[]) => void
  addAttendanceRecord: (record: AttendanceRecord) => void
  
  // Marks & Exams
  exams: Exam[]
  markEntries: MarkEntry[]
  setExams: (exams: Exam[]) => void
  setMarkEntries: (entries: MarkEntry[]) => void
  addMarkEntry: (entry: MarkEntry) => void
  
  // Financial
  invoices: Invoice[]
  setInvoices: (invoices: Invoice[]) => void
  
  // School Events
  schoolEvents: SchoolEvent[]
  setSchoolEvents: (events: SchoolEvent[]) => void
  
  // Teacher-specific state
  todaySchedule: Class[]
  isCheckedIn: boolean
  checkInTime: string | null
  setTodaySchedule: (schedule: Class[]) => void
  setCheckedIn: (checkedIn: boolean, time?: string | null) => void
  
  // Parent-specific state
  linkedStudents: Student[]
  setLinkedStudents: (students: Student[]) => void
}

export interface Notification {
  id: string
  title: string
  message: string
  type: 'info' | 'success' | 'warning' | 'error'
  timestamp: string
  read: boolean
}

const INDEXED_DB_NAME = 'sms_app_store'
const INDEXED_DB_STORE = 'zustand_persist'
const INDEXED_DB_VERSION = 1

const openPersistDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(INDEXED_DB_NAME, INDEXED_DB_VERSION)

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(INDEXED_DB_STORE)) {
        db.createObjectStore(INDEXED_DB_STORE)
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Failed to open IndexedDB'))
  })

// Real IndexedDB-backed storage for persisted app state, with localStorage fallback.
const indexedDBStorage: StateStorage = {
  getItem: async (name: string): Promise<string | null> => {
    if (typeof window === 'undefined') return null

    try {
      const db = await openPersistDb()
      const value = await new Promise<string | null>((resolve, reject) => {
        const tx = db.transaction(INDEXED_DB_STORE, 'readonly')
        const store = tx.objectStore(INDEXED_DB_STORE)
        const request = store.get(name)

        request.onsuccess = () => resolve((request.result as string | undefined) ?? null)
        request.onerror = () => reject(request.error || new Error('Failed to read IndexedDB value'))
        tx.oncomplete = () => db.close()
      })

      if (value !== null) return value

      // One-time fallback/migration from older localStorage persistence.
      const fallback = localStorage.getItem(name)
      if (fallback !== null) {
        await indexedDBStorage.setItem(name, fallback)
      }
      return fallback
    } catch (error) {
      console.error('Failed to read from IndexedDB, falling back to localStorage:', error)
      return localStorage.getItem(name)
    }
  },
  setItem: async (name: string, value: string): Promise<void> => {
    if (typeof window === 'undefined') return

    try {
      const db = await openPersistDb()
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(INDEXED_DB_STORE, 'readwrite')
        const store = tx.objectStore(INDEXED_DB_STORE)
        const request = store.put(value, name)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error || new Error('Failed to write IndexedDB value'))
        tx.oncomplete = () => db.close()
      })
    } catch (error) {
      console.error('Failed to write to IndexedDB, falling back to localStorage:', error)
      localStorage.setItem(name, value)
    }
  },
  removeItem: async (name: string): Promise<void> => {
    if (typeof window === 'undefined') return

    try {
      const db = await openPersistDb()
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(INDEXED_DB_STORE, 'readwrite')
        const store = tx.objectStore(INDEXED_DB_STORE)
        const request = store.delete(name)

        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error || new Error('Failed to delete IndexedDB value'))
        tx.oncomplete = () => db.close()
      })
    } catch (error) {
      console.error('Failed to remove from IndexedDB, falling back to localStorage:', error)
      localStorage.removeItem(name)
    }
  },
}

export const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Auth identity
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      
      // Auth Actions (backward compatible)
      setAuth: (user, accessToken, refreshToken) => {
        // Save tokens to localStorage
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem(TOKEN_STORAGE_KEY, accessToken)
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken)
          } catch (error) {
            console.error('Failed to save tokens to localStorage:', error)
          }
        }
        set({ user, accessToken, refreshToken, isAuthenticated: true })
      },
      
      clearAuth: () => {
        // Clear tokens from localStorage
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem(TOKEN_STORAGE_KEY)
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
          } catch (error) {
            console.error('Failed to remove tokens from localStorage:', error)
          }
        }
        
        set({ 
          user: null, 
          accessToken: null, 
          refreshToken: null,
          isAuthenticated: false,
          activeTerm: null,
          offlineQueue: [],
          notifications: [],
          // Clear SMS-specific state on logout
          classes: [],
          students: [],
          subjects: [],
          attendanceRecords: [],
          exams: [],
          markEntries: [],
          invoices: [],
          schoolEvents: [],
          todaySchedule: [],
          linkedStudents: [],
          isCheckedIn: false,
          checkInTime: null
        })
      },
      
      // New auth methods (aligned with auth slice)
      setCredentials: (user, accessToken, refreshToken) => {
        if (typeof window !== 'undefined') {
          try {
            // Clear any stale error data before setting new credentials
            localStorage.removeItem('sms_global_error')
            localStorage.removeItem('sms_error_timestamp')
            
            localStorage.setItem(TOKEN_STORAGE_KEY, accessToken)
            localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken)
          } catch (error) {
            console.error('Failed to save tokens to localStorage:', error)
          }
        }
        // Clear global error state when setting new credentials
        set({ 
          user, 
          accessToken, 
          refreshToken, 
          isAuthenticated: true,
          globalError: null 
        })
      },
      
      signOutSuccess: () => {
        if (typeof window !== 'undefined') {
          try {
            localStorage.removeItem(TOKEN_STORAGE_KEY)
            localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
          } catch (error) {
            console.error('Failed to remove tokens from localStorage:', error)
          }
        }
        
        set({ 
          user: null, 
          accessToken: null, 
          refreshToken: null,
          isAuthenticated: false,
          activeTerm: null,
          offlineQueue: [],
          notifications: [],
          classes: [],
          students: [],
          subjects: [],
          attendanceRecords: [],
          exams: [],
          markEntries: [],
          invoices: [],
          schoolEvents: [],
          todaySchedule: [],
          linkedStudents: [],
          isCheckedIn: false,
          checkInTime: null
        })
      },
      
      updateUser: (updates) => {
        const currentUser = get().user
        if (currentUser) {
          set({ user: { ...currentUser, ...updates } })
        }
      },
      
      syncFromSession: (session) => {
        console.log('[Zustand] syncFromSession called:', {
          hasSession: !!session,
          hasUser: !!session?.user,
          hasAccessToken: !!session?.accessToken,
          hasRefreshToken: !!session?.refreshToken,
          accessTokenLength: session?.accessToken?.length || 0
        })
        
        if (session?.user) {
          const user: User = {
            id: session.user.id,
            email: session.user.email || '',
            role: session.user.role as UserRole,
            firstName: session.user.name?.split(' ')[0] || '',
            lastName: session.user.name?.split(' ').slice(1).join(' ') || '',
            schoolId: session.user.schoolId || '',
            photoUrl: session.user.image,
          }
          
          // CRITICAL: Sync tokens from NextAuth session to Zustand store
          const accessToken = session.accessToken || null
          const refreshToken = session.refreshToken || null
          
          console.log('[Zustand] Extracted tokens from session:', {
            accessToken: accessToken ? `${accessToken.substring(0, 20)}...` : 'null',
            refreshToken: refreshToken ? `${refreshToken.substring(0, 20)}...` : 'null',
            accessTokenLength: accessToken?.length || 0,
            refreshTokenLength: refreshToken?.length || 0
          })
          
          // Save tokens to localStorage
          if (typeof window !== 'undefined' && accessToken && refreshToken) {
            try {
              localStorage.setItem(TOKEN_STORAGE_KEY, accessToken)
              localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken)
              console.log('[Zustand] Tokens saved to localStorage')
            } catch (error) {
              console.error('[Zustand] Failed to save tokens to localStorage:', error)
            }
          } else {
            console.warn('[Zustand] Tokens NOT saved - missing tokens:', {
              hasAccessToken: !!accessToken,
              hasRefreshToken: !!refreshToken,
              isClient: typeof window !== 'undefined'
            })
          }
          
          set({ 
            user, 
            accessToken, 
            refreshToken, 
            isAuthenticated: true,
            globalError: null 
          })
          
          console.log('[Zustand] Store updated with tokens:', {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken
          })
        } else {
          console.log('[Zustand] No session user - clearing tokens')
          // Clear tokens on logout
          if (typeof window !== 'undefined') {
            try {
              localStorage.removeItem(TOKEN_STORAGE_KEY)
              localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
              console.log('[Zustand] Tokens removed from localStorage')
            } catch (error) {
              console.error('[Zustand] Failed to remove tokens from localStorage:', error)
            }
          }
          set({ 
            user: null, 
            accessToken: null, 
            refreshToken: null, 
            isAuthenticated: false 
          })
        }
      },
      
      // App-wide Context
      activeTerm: null,
      
      // Term Actions
      setActiveTerm: (term) => {
        set({ activeTerm: term })
      },
      
      // Modals & Guards
      termUnpaidModalOpen: false,
      termUnpaidDetails: null,
      
      showTermUnpaidModal: (details) => {
        set({ 
          termUnpaidModalOpen: true, 
          termUnpaidDetails: details 
        })
      },
      
      hideTermUnpaidModal: () => {
        set({ 
          termUnpaidModalOpen: false, 
          termUnpaidDetails: null 
        })
      },
      
      // Offline Sync Engine
      isOffline: false,
      setOffline: (status) => {
        set({ isOffline: status })
      },
      offlineQueue: [],
      addToQueue: (item) => {
        const queue = get().offlineQueue
        set({ offlineQueue: [...queue, item] })
      },
      removeFromQueue: (id) => {
        const queue = get().offlineQueue
        set({ offlineQueue: queue.filter(item => item.id !== id) })
      },
      updateQueueItem: (id, updates) => {
        const queue = get().offlineQueue
        set({ 
          offlineQueue: queue.map(item => 
            item.id === id ? { ...item, ...updates } : item
          ) 
        })
      },
      clearQueue: () => {
        set({ offlineQueue: [] })
      },
      
      isSyncing: false,
      
      setSyncing: (status) => {
        set({ isSyncing: status })
      },
      
      // UI State
      sidebarOpen: false,
      
      setSidebarOpen: (open) => {
        set({ sidebarOpen: open })
      },
      
      // Global UI State
      globalLoading: false,
      globalLoadingMessage: null,
      globalError: null,
      
      setGlobalLoading: (loading, message) => {
        set({ globalLoading: loading, globalLoadingMessage: message || null })
      },
      
      setGlobalError: (error) => {
        set({ globalError: error })
      },
      
      clearGlobalError: () => {
        set({ globalError: null })
      },
      
      // Notification State
      notifications: [],
      
      addNotification: (notification) => {
        const newNotification: Notification = {
          ...notification,
          id: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
          read: false
        }
        
        const notifications = get().notifications
        set({ notifications: [newNotification, ...notifications].slice(0, 50) }) // Keep only 50 most recent
      },
      
      removeNotification: (id) => {
        const notifications = get().notifications
        set({ notifications: notifications.filter(n => n.id !== id) })
      },
      
      clearNotifications: () => {
        set({ notifications: [] })
      },

      // SMS-Specific State Implementation
      // Academic Data
      classes: [],
      students: [],
      subjects: [],
      
      setClasses: (classes) => {
        set({ classes })
      },
      
      setStudents: (students) => {
        set({ students })
      },
      
      setSubjects: (subjects) => {
        set({ subjects })
      },
      
      // Attendance
      attendanceRecords: [],
      
      setAttendanceRecords: (records) => {
        set({ attendanceRecords: records })
      },
      
      addAttendanceRecord: (record) => {
        const records = get().attendanceRecords
        set({ attendanceRecords: [...records, record] })
      },
      
      // Marks & Exams
      exams: [],
      markEntries: [],
      
      setExams: (exams) => {
        set({ exams })
      },
      
      setMarkEntries: (entries) => {
        set({ markEntries: entries })
      },
      
      addMarkEntry: (entry) => {
        const entries = get().markEntries
        set({ markEntries: [...entries, entry] })
      },
      
      // Financial
      invoices: [],
      
      setInvoices: (invoices) => {
        set({ invoices })
      },
      
      // School Events
      schoolEvents: [],
      
      setSchoolEvents: (events) => {
        set({ schoolEvents: events })
      },
      
      // Teacher-specific state
      todaySchedule: [],
      isCheckedIn: false,
      checkInTime: null,
      
      setTodaySchedule: (schedule) => {
        set({ todaySchedule: schedule })
      },
      
      setCheckedIn: (checkedIn, time) => {
        set({ 
          isCheckedIn: checkedIn,
          checkInTime: time || (checkedIn ? new Date().toISOString() : null)
        })
      },
      
      // Parent-specific state
      linkedStudents: [],
      
      setLinkedStudents: (students) => {
        set({ linkedStudents: students })
      }
    }),
    {
      name: PERSIST_STORE_NAME,
      storage: createJSONStorage(() => indexedDBStorage),
      partialize: (state) => ({
        // Only persist these fields to localStorage
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        activeTerm: state.activeTerm,
        offlineQueue: state.offlineQueue,
        notifications: state.notifications,
        // SMS-specific persistent state
        classes: state.classes,
        students: state.students,
        subjects: state.subjects,
        attendanceRecords: state.attendanceRecords,
        exams: state.exams,
        markEntries: state.markEntries,
        invoices: state.invoices,
        schoolEvents: state.schoolEvents,
        linkedStudents: state.linkedStudents,
        // Don't persist temporary state like todaySchedule, isCheckedIn, globalLoading, globalError
      }),
    }
  )
)
